import { DropzoneFile } from 'components/Dropzone/utils';
import { User } from './User';
import { DataRoom } from 'router/subrouters/DataRoom/models/DataRoom';
import { DataRoomNda } from 'router/subrouters/DataRoom/models/DataRoomNDA';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { ObjectValues } from 'types';

export type ShareableLinkMainInfo = {
  doesNotExist: boolean;
  hasExpired: boolean;

  hasAuthorized: boolean;
  doesNotHaveAccess: boolean;
  hasPassword: boolean;

  hasNDA: boolean;
  ndaStatus?: ShareableLinkMainInfoNDAStatus;

  hasTermsAndConditions: boolean;
  termsAndConditionsContent?: string;

  addedToWaitingRoom: boolean;
  rejectedFromWaitingRoom: boolean;

  firstName?: string;
  lastName?: string;
  email?: string;
  companyName?: string;
  authorizationToken?: string;

  requestedAccessAt?: Date;
  requestedPasswordAt?: Date;

  isEmailNotificationTurnOn?: boolean;
  isSMSNotificationTurnOn?: boolean;
  isPlatformNotificationTurnOn?: boolean;
};

type BaseDataRoomShareableLink = {
  name: string;

  linkAccess: DataRoomShareableLinkAccess;

  // for add_password
  password?: string;
  recipientEmailAddresses?: string[];

  linkDestination?: DataRoomShareableLinkDestination;
  optionalNote?: string;
  anyoneWithNonPublicDomain?: boolean;
  emailDomain?: string;

  applyForNewFilesInFolders: boolean;

  // security
  linkExpires: DataRoomShareableLinkExpires;
  linkExpiresDate?: Date;
  allowDownloads: boolean;
  downloadExpiresAfter?: number;

  includeRemoteTermination: boolean;
  includeWatermark: boolean;
  watermarkDiagonal?: boolean;
  watermarkHeaderAndFooter?: boolean;
  watermarkNameAndEmail?: boolean;
  watermarkIpAddress?: boolean;

  // terms and conditions
  includeTermsAndConditionProtection: boolean;
  termsAndConditionsContent?: string;

  // NDA
  includeNdaProtection: boolean;
  dataRoomNdaId?: string;
};

export type DataRoomShareableLink = BaseDataRoomShareableLink & {
  _id: string;

  fileIds: string[];
  folderIds: string[];

  // for specific_recipients
  // initial emailAddressesWithAccess === recipientEmailAddresses; but can be extended
  emailAddressesWithAccess: string[];

  isActive: boolean;

  //sort props
  sortName: string;
  accessNumber: number;
  lastAccessDate: Date;

  createdAt: Date;
  updatedAt: Date;

  userId: string;
  user: Partial<User> | null;

  dataRoomId: string;
  dataRoom: Partial<DataRoom> | null;

  // NDA
  dataRoomNda?: DataRoomNda;
};

export const DATA_ROOM_SHAREABLE_LINK_ACCESS = {
  ANYONE_WITH_LINK: 'anyone_with_link',
  ADD_PASSWORD: 'add_password',
  SPECIFIC_RECIPIENTS: 'specific_recipients',
} as const;

export type DataRoomShareableLinkAccess = ObjectValues<
  typeof DATA_ROOM_SHAREABLE_LINK_ACCESS
>;

export const DATA_ROOM_SHAREABLE_LINK_EXPIRES = {
  SEVEN_DAYS: 'seven_days',
  THIRTY_DAYS: 'thirty_days',
  OTHER: 'other',
} as const;

export type DataRoomShareableLinkExpires = ObjectValues<
  typeof DATA_ROOM_SHAREABLE_LINK_EXPIRES
>;

export const DATA_ROOM_SHAREABLE_LINK_DESTINATION = {
  WAITING_ROOM: 'waiting_room',
  DIRECT_ACCESS: 'direct_access',
} as const;

export type DataRoomShareableLinkDestination = ObjectValues<
  typeof DATA_ROOM_SHAREABLE_LINK_DESTINATION
>;

export type CreateDataRoomShareableLinkForm = BaseDataRoomShareableLink & {
  // NDA
  useExistingNda?: boolean;
  dataRoomNda?: DropzoneFile;

  recipientEmailAddressesUnsubmited?: string;
};

export type CreateDataRoomShareableLinkPayload =
  CreateDataRoomShareableLinkForm & {
    fileIds: string[];
    folderIds: string[];
  };

const DATA_ROOM_SHAREABLE_LINK_REQUEST_TYPE = {
  REQUEST_ACCESS: 'request_access',
  REQEUST_PASSWORD: 'request_password',
} as const;

type DataRoomShareableLinkRequestType = ObjectValues<
  typeof DATA_ROOM_SHAREABLE_LINK_REQUEST_TYPE
>;

const DATA_ROOM_SHAREABLE_LINK_REQUEST_STATUS = {
  REQUESTED: 'requested',
  GRANTED: 'granted',
  REJECTED: 'rejected',
} as const;

type DataRoomShareableLinkRequestStatus = ObjectValues<
  typeof DATA_ROOM_SHAREABLE_LINK_REQUEST_STATUS
>;

export type DataRoomShareableLinkReport = {
  _id: string;

  action: DataRoomShareableLinkReportAction;

  dataRoomShareableLinkConsumerId: string;
  dataRoomShareableLinkConsumer: Partial<DataRoomShareableLinkConsumer> | null;

  dataRoomNodeId: string;
  dataRoomNode: Partial<DataRoomNode> | null;

  dataRoomId: string;
  dataRoom: Partial<DataRoom> | null;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
};

export const DATA_ROOM_SHAREABLE_LINK_REPORT_ACTION = {
  VIEW: 'view',
  DOWNLOAD: 'download',
} as const;

export type DataRoomShareableLinkReportAction = ObjectValues<
  typeof DATA_ROOM_SHAREABLE_LINK_REPORT_ACTION
>;

export type DataRoomShareableLinkConsumer = {
  _id: string;

  canRevoke?: boolean;
  lastAccessNodeAt?: Date; // last time that user access Data room node

  email: string;
  firstName: string;
  lastName?: string;
  companyName?: string;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
};

export type DataRoomShareableLinkRequest = {
  _id: string;

  type: DataRoomShareableLinkRequestType;
  status: DataRoomShareableLinkRequestStatus;
  requestedAt: Date;

  dataRoomShareableLinkId: string;
  dataRoomShareableLink: DataRoomShareableLink;

  dataRoomShareableLinkConsumerId: string;
  dataRoomShareableLinkConsumer: DataRoomShareableLinkConsumer;

  createdAt: Date;
  updatedAt: Date;
};

export type DataRoomShareableLinkNodeReport = {
  _id: string;

  // Analytics
  numberOfViews: number;
  numberOfDownloads: number;
  numberOfSecondsSpent: number;
  sortName: string;
  lastAccessDate: Date;

  dataRoomId: string;

  dataRoomNodeId: string;
  dataRoomNode?: DataRoomNode;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
};

export const SHAREABLE_LINK_MAIN_INFO_NDA_STATUS = {
  READY_FOR_SIGNING: 'ready_for_signing',
  WAITING_FOR_CONNECTION: 'waiting_for_connection',
  WAITING_FOR_COUNTER_SIGN: 'waiting_for_counter_sign',
  REJECTED: 'rejected',
} as const;

export type ShareableLinkMainInfoNDAStatus = ObjectValues<
  typeof SHAREABLE_LINK_MAIN_INFO_NDA_STATUS
>;
